import { HUBS } from '@/pages/ai-team/hubs'
import {
  Activity,
  BellRing,
  Bot,
  Building2,
  CalendarDays,
  CreditCard,
  Crown,
  Database,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  type LucideIcon,
  Network,
  PenLine,
  ScanSearch,
  Settings,
  Sparkles,
  ToggleRight,
  Users,
  Workflow
} from 'lucide-react'

export interface NavLeaf {
  kind: 'leaf'
  title: string
  to: string
  icon: LucideIcon
  badgeKey?: string
  /** Optional sub-heading inside a parent group; a label shows where it changes. */
  section?: string
  /** Only the exact path counts as this leaf (no sub-paths). */
  exact?: boolean
  /** Extra path prefixes that also light this leaf up (e.g. settings tabs). */
  match?: string[]
}

export interface NavParent {
  kind: 'parent'
  title: string
  icon: LucideIcon
  defaultOpen?: boolean
  children: NavLeaf[]
}

export type NavItem = NavLeaf | NavParent

export const NAV: NavItem[] = [
  { kind: 'leaf', title: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  {
    kind: 'parent',
    title: 'Operations',
    icon: Sparkles,
    defaultOpen: true,
    children: [
      { kind: 'leaf', title: 'Feed', to: '/apps/feed', icon: FileText },
      {
        kind: 'leaf',
        title: 'Businesses',
        to: '/apps/businesses',
        icon: Building2
      },
      { kind: 'leaf', title: 'People', to: '/apps/people', icon: Users },
      { kind: 'leaf', title: 'Creators', to: '/apps/creators', icon: Crown },
      { kind: 'leaf', title: 'Events', to: '/apps/events', icon: CalendarDays }
    ]
  },
  {
    kind: 'parent',
    title: 'Money & settings',
    icon: CreditCard,
    defaultOpen: true,
    children: [
      {
        kind: 'leaf',
        title: 'Pricing plans',
        to: '/apps/pricing',
        icon: CreditCard
      },
      {
        kind: 'leaf',
        title: 'Feature flags',
        to: '/super-powers',
        icon: ToggleRight
      }
    ]
  },
  {
    kind: 'parent',
    title: 'AI team',
    icon: Network,
    defaultOpen: true,
    children: [
      { kind: 'leaf', title: 'Overview', to: '/apps/ai-team', icon: LayoutGrid, exact: true },
      ...HUBS.map((h): NavLeaf => ({ kind: 'leaf', title: h.title, to: `/apps/ai-team/team/${h.id}`, icon: h.icon, section: 'Teams' })),
      { kind: 'leaf', title: 'Blog writer', to: '/apps/ai-team/blogger', icon: PenLine, section: 'Teams' },
      { kind: 'leaf', title: 'Needs you', to: '/apps/ai-team/inbox', icon: BellRing, section: 'You' },
      { kind: 'leaf', title: 'Calendar', to: '/apps/ai-team/calendar', icon: CalendarDays, section: 'You' },
      { kind: 'leaf', title: 'Workflows', to: '/apps/ai-team/workflows', icon: Workflow, section: 'You' },
      {
        kind: 'leaf',
        title: 'Settings',
        to: '/apps/ai-team/jobs',
        icon: Settings,
        section: 'You',
        match: [
          '/apps/ai-team/settings',
          '/apps/ai-team/knows',
          '/apps/ai-team/connections',
          '/apps/ai-team/models',
          '/apps/ai-team/routines',
          '/apps/ai-team/activity'
        ]
      }
    ]
  },
  {
    kind: 'parent',
    title: 'Automation',
    icon: Bot,
    defaultOpen: false,
    children: [
      { kind: 'leaf', title: 'Promoters', to: '/controls/bots', icon: Bot },
      {
        kind: 'leaf',
        title: 'XOB Staff',
        to: '/controls/staff-agents',
        icon: Activity
      },
      {
        kind: 'leaf',
        title: 'Scrapers',
        to: '/controls/scrapers',
        icon: Database
      },
      {
        kind: 'leaf',
        title: 'Web scraper',
        to: '/controls/web-scraper',
        icon: ScanSearch
      }
    ]
  }
]

export interface NavSearchResult extends NavLeaf {
  parentTitle?: string
}

export function flattenLeaves(nav: NavItem[] = NAV): NavSearchResult[] {
  const out: NavSearchResult[] = []
  for (const item of nav) {
    if (item.kind === 'leaf') out.push({ ...item })
    else
      for (const child of item.children)
        out.push({ ...child, parentTitle: item.title })
  }
  return out
}

function matchesPath(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

/** Length of the most specific path of this leaf that matches, or -1. */
function leafMatchLength(pathname: string, leaf: NavLeaf): number {
  if (leaf.exact || leaf.to === '/') return pathname === leaf.to ? leaf.to.length : -1
  let best = -1
  for (const p of [leaf.to, ...(leaf.match ?? [])]) {
    if (p.length > best && matchesPath(pathname, p)) best = p.length
  }
  return best
}

let allLeaves: NavSearchResult[] | null = null

/** The leaf for this path: the most specific match wins, so /apps/ai-team/chat doesn't light up /apps/ai-team. */
export function findActiveLeaf(pathname: string): NavSearchResult | undefined {
  allLeaves ??= flattenLeaves()
  let found: NavSearchResult | undefined
  let bestLen = -1
  for (const leaf of allLeaves) {
    const len = leafMatchLength(pathname, leaf)
    if (len > bestLen) {
      bestLen = len
      found = leaf
    }
  }
  return found
}

export function isLeafActive(pathname: string, leaf: NavLeaf): boolean {
  return findActiveLeaf(pathname)?.to === leaf.to
}
